import { useState, useEffect, useRef } from 'react';
import { X, Search, Plane, Users, Navigation, Anchor, Radar } from 'lucide-react';
import { cn } from '@/utils/cn';
import { airportApi, routeApi, crewApi } from '@/services/notesApi';
import type { Airport, Route, CrewMember, NotePhase } from '@/types/notes.types';

interface AssociationSelectorProps {
  phase?: NotePhase;
  selectedRouteId?: number | null;
  onRouteChange: (routeId: number | null) => void;
  selectedDepartureAirportCode?: string | null;
  onDepartureAirportChange: (code: string | null) => void;
  selectedArrivalAirportCode?: string | null;
  onArrivalAirportChange: (code: string | null) => void;
  selectedCrewIds?: number[];
  onCrewChange?: (ids: number[]) => void;
  className?: string;
}

const PHASE_CONFIG: Record<NotePhase, { label: string; icons: React.ReactNode }> = {
  takeoff: { label: '起飞', icons: <Navigation className="w-4 h-4" /> },
  cruise: { label: '巡航', icons: <Plane className="w-4 h-4" /> },
  landing: { label: '落地', icons: <Anchor className="w-4 h-4" /> },
  crew: { label: '机组', icons: <Users className="w-4 h-4" /> },
  general: { label: '通用', icons: <Radar className="w-4 h-4" /> },
};

export function AssociationSelector({
  phase = 'general',
  selectedRouteId,
  onRouteChange,
  selectedDepartureAirportCode,
  onDepartureAirportChange,
  selectedArrivalAirportCode,
  onArrivalAirportChange,
  selectedCrewIds = [],
  onCrewChange,
  className,
}: AssociationSelectorProps) {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [airports, setAirports] = useState<Airport[]>([]);
  const [crew, setCrew] = useState<CrewMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      routeApi.getRoutes({ pageSize: 100 }),
      airportApi.getAirports({ simple: true, pageSize: 500 }),
      crewApi.getCrewMembers({ pageSize: 500 }),
    ])
      .then(([r, a, c]) => {
        setRoutes(r.data.results || []);
        setAirports(a.data.results || []);
        setCrew(c.data.results || []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className={cn('space-y-4 animate-pulse', className)}>
        <div className="h-10 bg-gray-100 rounded-lg" />
        <div className="h-10 bg-gray-100 rounded-lg" />
      </div>
    );
  }

  const config = PHASE_CONFIG[phase];

  return (
    <div className={cn('space-y-4', className)}>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
        {config.icons}
        <span>{config.label}关联</span>
      </div>

      {/* 航线：巡航/通用 */}
      {(phase === 'cruise' || phase === 'general') && (
        <RouteSelector routes={routes} selectedRouteId={selectedRouteId} onRouteChange={onRouteChange} />
      )}

      {/* 起飞机场：起飞/通用 */}
      {(phase === 'takeoff' || phase === 'general') && (
        <AirportSelector
          airports={airports}
          label="起飞机场"
          placeholder="搜索起飞机场..."
          selectedCode={selectedDepartureAirportCode}
          onChange={onDepartureAirportChange}
          icon={<Navigation className="w-4 h-4 text-blue-500" />}
        />
      )}

      {/* 着陆机场：落地/通用 */}
      {(phase === 'landing' || phase === 'general') && (
        <AirportSelector
          airports={airports}
          label="着陆机场"
          placeholder="搜索着陆机场..."
          selectedCode={selectedArrivalAirportCode}
          onChange={onArrivalAirportChange}
          icon={<Anchor className="w-4 h-4 text-green-500" />}
        />
      )}

      {/* 机组：机组/通用 */}
      {(phase === 'crew' || phase === 'general') && onCrewChange && (
        <CrewSelector crew={crew} selectedIds={selectedCrewIds} onChange={onCrewChange} />
      )}
    </div>
  );
}

// =============================================================================
// RouteSelector
// =============================================================================

function RouteSelector({
  routes,
  selectedRouteId,
  onRouteChange,
}: {
  routes: Route[];
  selectedRouteId?: number | null;
  onRouteChange: (routeId: number | null) => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = routes.find((r) => r.id === selectedRouteId);
  const filtered = routes.filter(
    (r) =>
      r.codePair?.toLowerCase().includes(search.toLowerCase()) ||
      r.displayName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1">航线</label>
      {selected ? (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <Plane className="w-4 h-4 text-blue-600" />
          <span className="text-sm font-medium text-blue-700">
            {selected.displayName || selected.codePair}
          </span>
          <button type="button" onClick={() => onRouteChange(null)} className="ml-auto p-1 hover:bg-blue-100 rounded">
            <X className="w-4 h-4 text-blue-600" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder="搜索航线..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {open && filtered.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filtered.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => {
                    onRouteChange(r.id);
                    setSearch('');
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                >
                  {r.displayName || r.codePair}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// AirportSelector (single)
// =============================================================================

function AirportSelector({
  airports,
  label,
  placeholder,
  selectedCode,
  onChange,
  icon,
}: {
  airports: Airport[];
  label: string;
  placeholder: string;
  selectedCode?: string | null;
  onChange: (code: string | null) => void;
  icon: React.ReactNode;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = airports.find((a) => a.iataCode === selectedCode);
  const filtered = airports.filter(
    (a) =>
      a.iataCode?.toLowerCase().includes(search.toLowerCase()) ||
      a.nameZh?.toLowerCase().includes(search.toLowerCase()) ||
      a.cityZh?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1">{label}</label>
      {selected ? (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          {icon}
          <span className="text-sm font-medium text-blue-700">
            {selected.iataCode} {selected.nameZh}
          </span>
          <button type="button" onClick={() => onChange(null)} className="ml-auto p-1 hover:bg-blue-100 rounded">
            <X className="w-4 h-4 text-blue-600" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            placeholder={placeholder}
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {open && filtered.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {filtered.map((a) => (
                <button
                  key={a.iataCode}
                  type="button"
                  onClick={() => {
                    onChange(a.iataCode);
                    setSearch('');
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                >
                  {a.iataCode} {a.nameZh} {a.cityZh}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// CrewSelector
// =============================================================================

function CrewSelector({
  crew,
  selectedIds,
  onChange,
}: {
  crew: CrewMember[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = crew.filter(
    (c) =>
      !selectedIds.includes(c.id) &&
      (c.name?.toLowerCase().includes(search.toLowerCase()) ||
        c.displayName?.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div ref={ref} className="relative">
      <label className="block text-xs font-medium text-gray-500 mb-1">
        <Users className="w-4 h-4 inline mr-1" />
        机组人员
      </label>
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {selectedIds.map((id) => {
            const c = crew.find((x) => x.id === id);
            return (
              <span
                key={id}
                className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 border border-purple-200 rounded text-xs text-purple-700"
              >
                {c ? c.displayName : id}
                <button type="button" onClick={() => onChange(selectedIds.filter((x) => x !== id))}>
                  <X className="w-3 h-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="搜索机组人员..."
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {open && filtered.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => {
                  onChange([...selectedIds, c.id]);
                  setSearch('');
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              >
                {c.displayName}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
